import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useMemo, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import { useAddTechnicianToObjectModalStore } from './AddTechnicianToObjectModalStore';

import { TechnicianSiteFormValues, technicianSiteSchema } from 'api/clients/types';
import {
  TechnicianFormData,
  technicianSchema,
  userOnClientCreationResponse,
} from 'api/clients/clientSmartCreation/types';
import useAddTechnicianToObject from 'hooks/Object/useAddTechnicianToObject';
import useGetTechnicianToObject from 'hooks/Object/useGetTechnicianToObject';
import { MLSelect } from 'components/Select/Select';
import { MLInput } from 'components/Input/Input';
import { addTechnicianForClient } from 'api/clients/clientSmartCreation/addTechnicianForClient';

interface AddTechnicianModalProps {
  isOpen: boolean;
  close: () => void;
}

const AddTechnicianModalToObject: FC<AddTechnicianModalProps> = ({ isOpen, close }) => {
  const { t } = useTranslation();

  const { objectSlug } = useAddTechnicianToObjectModalStore();

  const {
    data: technicianList,
    refetch: refetchTechList,
    isLoading: isTechListLoading,
  } = useGetTechnicianToObject(objectSlug);

  const { AddTechnicianToObject } = useAddTechnicianToObject();

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const {
    handleSubmit: handleSelectSubmit,
    control: selectControl,
    reset: resetSelect,
    watch: watchSelect,
    setValue: setSelectValue,
    formState: { errors: selectErrors },
  } = useForm<TechnicianSiteFormValues>({
    resolver: zodResolver(technicianSiteSchema),
    defaultValues: {
      technician: '',
      is_lead: true,
    },
  });

  const selectedIsLead = watchSelect('is_lead');

  const selectOptions = useMemo(() => {
    if (!technicianList || technicianList.length === 0) {
      return [{ value: '', label: t('Modals.addTechnicianModal.noTechnicians') }];
    }

    return [{ value: '', label: t('Modals.addTechnicianModal.selectTechnician') }].concat(
      technicianList.map(tp => ({
        value: tp.id.toString(),
        label: tp.label,
      })),
    );
  }, [technicianList, t]);

  const onAddSelectedToObject = (data: TechnicianSiteFormValues) => {
    AddTechnicianToObject(
      {
        objectSlug,
        technician: data,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('Modals.success-edit'), { variant: 'success' });
          onCloseModal();
        },
        onError: (error: any) => {
          const msg = error?.response?.data?.technician?.[0] || error?.response?.data?.detail;

          enqueueSnackbar(msg, { variant: 'error' });
        },
      },
    );
  };

  const {
    handleSubmit: handleCreateSubmit,
    control: createControl,
    reset: resetCreate,
    setError: setCreateError,
    formState: { errors: createErrors },
  } = useForm<TechnicianFormData>({
    resolver: zodResolver(technicianSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      phone_number: '',
      email: '',
      password: '',
      confirm_password: '',
    },
  });

  const { mutate: createTechnician, isLoading: isCreating } = useMutation((payload: TechnicianFormData) =>
    addTechnicianForClient(payload),
  );

  const onCreateNewTechnician = (payload: TechnicianFormData) => {
    createTechnician(payload, {
      onSuccess: async (created: userOnClientCreationResponse) => {
        enqueueSnackbar(t('Modals.manage-technician.successCreate'), { variant: 'success' });

        // 1) refresh list
        await refetchTechList();

        // 2) select created tech in dropdown
        setSelectValue('technician', String(created.id), { shouldValidate: true });

        // 3) keep leader flag as user set in select block (same checkbox)
        setSelectValue('is_lead', selectedIsLead, { shouldValidate: true });

        // 4) close create form block
        setIsCreateOpen(false);
        resetCreate();

        // 5) OPTIONAL: auto-add created tech immediately
        AddTechnicianToObject(
          {
            objectSlug,
            technician: {
              technician: String(created.id),
              is_lead: selectedIsLead,
            } as any,
          },
          {
            onSuccess: () => {
              enqueueSnackbar(t('Modals.success-edit'), { variant: 'success' });
              onCloseModal();
            },
            onError: (error: any) => {
              const msg = error?.response?.data?.technician?.[0] || error?.response?.data?.detail;

              enqueueSnackbar(msg, { variant: 'error' });
              // keep modal open so user can click Add again
            },
          },
        );
      },

      onError: (error: any) => {
        const data = error?.response?.data;

        if (data?.first_name) setCreateError('first_name', { message: data.first_name[0] });
        if (data?.last_name) setCreateError('last_name', { message: data.last_name[0] });
        if (data?.email) setCreateError('email', { message: data.email[0] });
        if (data?.password) setCreateError('password', { message: data.password[0] });
        if (data?.confirm_password) setCreateError('confirm_password', { message: data.confirm_password[0] });

        if (!data) {
          enqueueSnackbar(t('Modals.manage-technician.failCreate'), { variant: 'error' });
        }
      },
    });
  };

  const onCloseModal = () => {
    close();
    setIsCreateOpen(false);
    resetSelect();
    resetCreate();
  };

  const dropdownDisabled = isCreateOpen || isTechListLoading || !technicianList || technicianList.length === 0;

  return (
    <Modal isOpen={isOpen} toggle={onCloseModal} size="lg" backdrop="static" keyboard={false}>
      <ModalHeader className="border-bottom pb-2 pt-3 mx-3" toggle={onCloseModal}>
        {t('Modals.addTechnicianModal.title')}
      </ModalHeader>

      <ModalBody>
        <form className="col-12" onSubmit={e => e.preventDefault()}>
          <div className="d-flex gap-2 justify-content-center align-items-end">
            <div className="flex-fill">
              <Controller
                control={selectControl}
                name="technician"
                render={({ field }) => (
                  <MLSelect
                    {...field}
                    label={t('Modals.addTechnicianModal.technician')}
                    error={selectErrors?.technician?.message}
                    value={field.value}
                    required
                    onChange={selectedOption => field.onChange(selectedOption)}
                    isDisabled={dropdownDisabled}
                    options={selectOptions}
                  />
                )}
              />
            </div>

            <Button
              type="button"
              className="btn btn-sm btn-ghost-success"
              onClick={() => setIsCreateOpen(v => !v)}
              style={{ width: 40, height: 40 }}
            >
              {isCreateOpen ? <span className="fs-5">−</span> : <span className="fs-5">+</span>}
            </Button>
          </div>

          <Controller
            control={selectControl}
            name="is_lead"
            render={({ field }) => (
              <div className="d-flex gap-2 mt-2 z-n1">
                <input
                  type="checkbox"
                  className="z-n1"
                  checked={field.value}
                  onChange={() => field.onChange(!field.value)}
                />
                <span className="d-flex align-items-center">{t('Modals.addTechnicianModal.team-leader')}</span>
              </div>
            )}
          />
        </form>

        {isCreateOpen && (
          <div className="mt-4 pt-3 border-top">
            <form className="col-12" onSubmit={e => e.preventDefault()}>
              <div className="row mb-3">
                <div className="col-md-6">
                  <Controller
                    control={createControl}
                    name="first_name"
                    render={({ field }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-technician.firstName')}
                        error={createErrors.first_name?.message}
                        required
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={createControl}
                    name="last_name"
                    render={({ field }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-technician.lastName')}
                        error={createErrors.last_name?.message}
                        required
                      />
                    )}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Controller
                    control={createControl}
                    name="email"
                    render={({ field }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-technician.email')}
                        type="email"
                        error={createErrors.email?.message}
                        required
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={createControl}
                    name="phone_number"
                    render={({ field }) => (
                      <MLInput
                        {...field}
                        required
                        label={t('Modals.manage-technician.phone')}
                        error={createErrors.phone_number?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-6">
                  <Controller
                    control={createControl}
                    name="password"
                    render={({ field }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-technician.password')}
                        type="password"
                        error={createErrors.password?.message}
                        required
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={createControl}
                    name="confirm_password"
                    render={({ field }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-technician.confirmPassword')}
                        type="password"
                        error={createErrors.confirm_password?.message}
                        required
                      />
                    )}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  type="button"
                  className="btn btn-ghost-danger"
                  onClick={() => {
                    setIsCreateOpen(false);
                    resetCreate();
                  }}
                >
                  {t('Modals.delete-modal.cancel')}
                </Button>

                <Button
                  type="button"
                  className="btn btn-ghost-success"
                  onClick={handleCreateSubmit(onCreateNewTechnician)}
                >
                  {isCreating ? t('Modals.manage-client.creating') : t('Modals.manage-technician.createTechnician')}
                </Button>
              </div>
            </form>
          </div>
        )}
      </ModalBody>

      {!isCreateOpen && (
        <ModalFooter>
          <Button className="btn btn-ghost-danger" onClick={onCloseModal}>
            {t('Modals.delete-modal.cancel')}
          </Button>

          <Button type="button" className="btn btn-ghost-success" onClick={handleSelectSubmit(onAddSelectedToObject)}>
            {t('Modals.addTechnicianModal.add')}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default AddTechnicianModalToObject;
